/**
 * ColumnCustomizer - Dialog for customizing visible columns in LeadsTableV2
 */

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Box,
  Typography,
  IconButton,
  Chip,
  Stack,
} from '@mui/material';
import { Close as CloseIcon, ViewColumn as ViewColumnIcon } from '@mui/icons-material';
import { LEAD_THEME as T } from '../../../utils/lead_helper_v2';

const columnGroups = {
  'Basic Information': ['lead_id', 'name', 'email', 'mobile'],
  'Dates & Timeline': ['created_at', 'first_contact_at', 'last_contacted_at', 'lead_age', 'time_since_last_contact'],
  'Status & Stage': ['stage', 'status', 'sales_owner', 'is_reassigned'],
  'Activities & Engagement': ['next_followup', 'next_visit', 'last_completed_visit', 'site_visit_count', 'last_note', 'all_notes'],
  'Call Analytics': ['total_connected_calls', 'total_talktime'],
  'Additional Details': ['source', 'tags', 'channel_partner', 'budget', 'lead_metadata', 'not_interested_reason'],
};

const defaultColumns = [
  'lead_id', 'name', 'email', 'mobile', 'created_at', 'last_contacted_at',
  'stage', 'status', 'source', 'sales_owner'
];

const ColumnCustomizer = ({ open, onClose, availableColumns, visibleColumns, onSave }) => {
  const [selectedColumns, setSelectedColumns] = useState(visibleColumns);

  useEffect(() => {
    setSelectedColumns(visibleColumns || []);
  }, [visibleColumns, open]);

  const handleToggle = (columnId) => {
    setSelectedColumns(prev => {
      if (prev.includes(columnId)) {
        return prev.filter(id => id !== columnId);
      }
      return [...prev, columnId];
    });
  };

  const handleSelectAll = () => {
    setSelectedColumns((availableColumns || []).map(col => col.id));
  };

  const handleDeselectAll = () => {
    setSelectedColumns([]);
  };

  const handleReset = () => {
    setSelectedColumns(defaultColumns);
  };

  const handleSave = () => {
    onSave(selectedColumns);
    onClose();
  };

  const totalAvailable = (availableColumns || []).length;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: '0 24px 48px rgba(0,0,0,0.12)',
          overflow: 'hidden',
          border: `1px solid ${T.border}`,
        },
      }}
      sx={{ zIndex: 9999 }}
    >
      {/* Header */}
      <DialogTitle
        sx={{
          px: 2.5,
          py: 2,
          background: T.bg,
          borderBottom: `1px solid ${T.border}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: 2,
              background: T.greenSf,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <ViewColumnIcon sx={{ fontSize: 22, color: T.green }} />
          </Box>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, color: T.ink, fontSize: '1.125rem' }}>
              Customize columns
            </Typography>
            <Typography variant="body2" sx={{ color: T.muted, fontWeight: 500, mt: 0.25 }}>
              Choose which columns to show in the leads table
            </Typography>
          </Box>
        </Box>
        <IconButton
          onClick={onClose}
          size="small"
          sx={{
            color: T.muted,
            '&:hover': { color: T.ink2, bgcolor: T.borderL },
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ px: 2.5, py: 2, bgcolor: T.white }}>
        {/* Quick actions + count */}
        <Stack direction="row" alignItems="center" flexWrap="wrap" gap={1} sx={{ mb: 2 }}>
          <Chip
            label="Select all"
            size="small"
            onClick={handleSelectAll}
            sx={{
              height: 28,
              fontWeight: 600,
              fontSize: '0.75rem',
              bgcolor: T.borderL,
              color: T.ink2,
              '&:hover': { bgcolor: T.greenSf, color: T.green },
            }}
          />
          <Chip
            label="Deselect all"
            size="small"
            onClick={handleDeselectAll}
            sx={{
              height: 28,
              fontWeight: 600,
              fontSize: '0.75rem',
              bgcolor: T.borderL,
              color: T.ink2,
              '&:hover': { bgcolor: T.borderL, color: T.subtle },
            }}
          />
          <Chip
            label="Reset to default"
            size="small"
            onClick={handleReset}
            sx={{
              height: 28,
              fontWeight: 600,
              fontSize: '0.75rem',
              bgcolor: T.borderL,
              color: T.ink2,
              '&:hover': { bgcolor: T.greenSf, color: T.green },
            }}
          />
          <Box sx={{ ml: 'auto' }}>
            <Typography variant="caption" sx={{ fontWeight: 700, color: T.muted }}>
              {selectedColumns.length} of {totalAvailable} selected
            </Typography>
          </Box>
        </Stack>

        {/* Column groups */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {Object.entries(columnGroups).map(([groupName, columnIds]) => {
            const groupColumns = columnIds
              .map(id => (availableColumns || []).find(col => col.id === id))
              .filter(Boolean);

            if (groupColumns.length === 0) return null;

            return (
              <Box
                key={groupName}
                sx={{
                  p: 1.5,
                  borderRadius: 2,
                  bgcolor: T.bg,
                  border: `1px solid ${T.borderL}`,
                }}
              >
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontWeight: 700,
                    fontSize: '0.6875rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.06em',
                    color: T.muted,
                    mb: 1.25,
                    px: 0.5,
                  }}
                >
                  {groupName}
                </Typography>
                <FormGroup sx={{ gap: 0 }}>
                  {groupColumns.map(column => (
                    <FormControlLabel
                      key={column.id}
                      control={
                        <Checkbox
                          checked={selectedColumns.includes(column.id)}
                          onChange={() => handleToggle(column.id)}
                          size="small"
                          sx={{
                            color: T.muted,
                            '&.Mui-checked': { color: T.green },
                            padding: '4px',
                          }}
                        />
                      }
                      label={
                        <Typography variant="body2" sx={{ fontWeight: 500, color: T.ink2, fontSize: '0.8125rem' }}>
                          {column.label}
                        </Typography>
                      }
                      sx={{
                        m: 0,
                        py: 0.5,
                        px: 0.5,
                        borderRadius: 1,
                        '&:hover': { bgcolor: T.greenSf },
                      }}
                    />
                  ))}
                </FormGroup>
              </Box>
            );
          })}
        </Box>
      </DialogContent>

      {/* Footer */}
      <DialogActions
        sx={{
          px: 2.5,
          py: 2,
          gap: 1.5,
          borderTop: `1px solid ${T.border}`,
          bgcolor: T.bg,
        }}
      >
        <Button
          onClick={onClose}
          variant="outlined"
          sx={{
            fontWeight: 600,
            borderRadius: 2,
            borderColor: T.border,
            color: T.ink2,
            '&:hover': { borderColor: T.subtle, bgcolor: T.borderL },
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={selectedColumns.length === 0}
          sx={{
            fontWeight: 600,
            borderRadius: 2,
            bgcolor: T.green,
            boxShadow: '0 2px 8px rgba(10,107,62,0.25)',
            '&:hover': { bgcolor: T.greenMd, boxShadow: '0 4px 12px rgba(10,107,62,0.3)' },
          }}
        >
          Apply changes
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ColumnCustomizer;
